import {Component, ComponentRef, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {NxDialogService, NxModalRef} from '@aposin/ng-aquila/modal';
import {Subscription} from 'rxjs';
import {distinctUntilChanged, filter, first, tap} from 'rxjs/operators';
import {DynamicComponentObject} from '../dynamic-components/interfaces/dynamic-component-object';
import {ModalConfirmReceiptComponent} from './components/modal-confirm-receipt/modal-confirm-receipt.component';
import {ModalLoadingComponent} from './components/modal-loading/modal-loading.component';
import {FieldColumns} from './enums/field-columns';
import {FormType} from './enums/form-type';
import {
  ApiDeleteUploadFileObjectFormatted,
  ApiUploadObjectFormatted,
  FormReturn,
} from './interfaces/api-object-formatted';
import {
  FormAllData,
  FormFieldDataBase,
  FormParts,
  FormStep,
  FormUnknown,
} from './interfaces/forms/form-element';
import {ModalConfirmDataElement} from './interfaces/modal-confirm-data';
import {StepEventLike} from './interfaces/step-event-like';
import {formTypeComponent} from './models/form-model';
import {FormService} from './services/form/form.service';
import {UploadService} from './services/upload/upload.service';
import {GenericFieldDirective} from './directives/generic-field/generic-field.directive';
import {FormComponent} from './components/form/form.component';
import {DynamicComponentBaseComponent} from '../dynamic-components/components/dynamic-component-base/dynamic-component-base.component';

@Component({
    selector: 'az-form',
    templateUrl: './az-forms.component.html',
    styleUrls: ['./az-forms.component.scss'],
    standalone: false
})
export class AzFormsComponent implements OnInit {
  private _formData!: FormParts<FormAllData | FormStep>;
  get formData(): FormParts<FormAllData | FormStep> {
    return this._formData;
  }
  @Input() set formData(val: FormParts<FormAllData | FormStep>) {
    if (this.formData !== val && val) {
      this._formData = Object.assign({}, val);
      this.setUpForm(val);
    }
  }

  private _captcha!: boolean;
  get captcha(): boolean {
    return this._captcha;
  }
  @Input() set captcha(val) {
    if (this._captcha !== val) {
      this._captcha = val;
    }
  }
  @Input() showLoader = true;
  @Input() modalSuccessText!: ModalConfirmDataElement;
  @Input() resetFormOnSubmit = true;

  @Input() submitButtonStyle = {} as {text: string; blockStyle?: boolean};

  @Input() stepNameOverride: string = 'unknownStep';

  @Output() formResponse: EventEmitter<FormReturn> = new EventEmitter<FormReturn>();

  @Output() uploadFileResponse: EventEmitter<ApiUploadObjectFormatted> =
    new EventEmitter<ApiUploadObjectFormatted>();
  @Output() additionalButtonEvent: EventEmitter<unknown> = new EventEmitter<unknown>();

  @Output() removeUploadFile: EventEmitter<ApiDeleteUploadFileObjectFormatted> =
    new EventEmitter<ApiDeleteUploadFileObjectFormatted>();

  @Output() formChanges: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  @Output() stepChanges: EventEmitter<StepEventLike> = new EventEmitter<StepEventLike>();

  @Output() generatedFormGroup: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  @Output() componentCreated: EventEmitter<ComponentRef<GenericFieldDirective>[]> =
    new EventEmitter();

  formSubscription!: Subscription;
  formPartSubscription!: Subscription;
  formSubmitSubscription!: Subscription;
  uploadSubscriber!: Subscription;
  deleteUploadedFileSubscriber!: Subscription;
  formstepSubscriber!: Subscription;

  backupFormData: FormFieldDataBase[] = [];
  defaultColumn = FieldColumns.HALF;
  showSubmit = false;

  hasRequiredError = false;
  hasFillError = false;

  public allFormData!: FormGroup;
  baseFormGroup!: FormGroup;

  formDataList: FormAllData[][] = [[]];

  modal!: NxModalRef<ModalConfirmReceiptComponent>;

  dynamicElement!: DynamicComponentObject<{
    formData: FormParts<FormAllData | FormStep>;
    formGroup: FormGroup;
  }>;

  valueChangesObserver$!: Subscription;
  lastValues: string = '';
  isStepChange: boolean = false;

  constructor(
    private dialogService: NxDialogService,
    private formService: FormService,
    private uploadService: UploadService,
  ) {}

  setUpForm(formPart: FormParts<FormAllData | FormStep>) {
    const {form, submit} = formPart;
    const type = this.formService.getFormType(form);
    this.showSubmit = typeof submit.hidden !== 'undefined' ? submit.hidden : false;

    this.allFormData =
      type === FormType.STEP
        ? this.formService.generateBaseStepsFormGroup(form as unknown as FormStep[])
        : this.formService.generateFormGroup(
            formPart as unknown as FormParts<FormAllData>,
            false,
            this.stepNameOverride,
          );
    if (this.generatedFormGroup.observed) {
      this.generatedFormGroup.emit(this.allFormData);
    }
    this.formService.saveFormGroup(this.allFormData);
    this.dynamicElement = {
      component: formTypeComponent[type],
      data: {
        formData: this.formData,
        formGroup: this.allFormData,
      },
    };
  }

  ngOnInit(): void {
    this.formPartSubscription = this.formService.getFormPart().subscribe((res: FormAllData[]) => {
      this.showSubmit = res.length > 0;
    });

    if (this.uploadFileResponse.observed) {
      this.uploadSubscriber = this.uploadService.getUploadRequest().subscribe(
        (res) => {
          this.uploadFileResponse.emit(res);
        },
        (err) => {
          console.error(err);
        },
      );
    }

    if (this.removeUploadFile.observed) {
      this.deleteUploadedFileSubscriber = this.uploadService.getDeleteFile().subscribe((res) => {
        if (this.removeUploadFile.observed) {
          this.removeUploadFile.emit(res);
        } else {
          res.uploadResolver.next(true);
        }
      });
    }

    this.formSubmitSubscription = this.formService
      .getCompletedForm()
      .subscribe(({showModal, resetForm}) => {
        if (showModal) {
          this.openModal(false, this.modalSuccessText);
        } else {
          this.dialogService.closeAll();
        }
        if (resetForm) {
          this.allFormData.reset();
          // this.allFormData.reset('implement', { emitEvent: false });
        }
      });
    if (this.stepChanges.observed) {
      this.formstepSubscriber = this.formService
        .getFormStepInteract()
        .pipe(distinctUntilChanged())
        .subscribe((res) => {
          this.isStepChange = true;
          res.changeStep
            .pipe(
              first(),
              tap((_) => {
                this.isStepChange = false;
              }),
            )
            .subscribe();
          this.stepChanges.emit(res);
        });
    }
    this.checkChanges();
  }

  onSubmit() {
    if (this.allFormData.valid) {
      if (this.showLoader) {
        this.openModal(true);
      }
      this.formService.setForm(this.allFormData).subscribe(
        (res) => {
          this.formResponse.emit(res);
        },
        (err) => {
          console.error(err);
          this.formResponse.emit(err);
        },
      );
    } else {
      this.checkErrors(this.allFormData);
    }
  }

  checkChanges() {
    if (this.formChanges.observed && !this.valueChangesObserver$) {
      this.valueChangesObserver$ = this.allFormData.valueChanges
        .pipe(
          distinctUntilChanged(),
          filter((res) => {
            if (this.isStepChange) {
              return null;
            }
            return res;
          }),
          tap((e) => {
            this.checkErrors(this.allFormData);
            if (this.formChanges.observed) {
              this.formChanges.emit(this.allFormData);
            }
          }),
        )
        .subscribe();
    }
    // this.allFormData.valueChanges
    //   .pipe(
    //     distinctUntilChanged(),
    //     filter((res) => {
    //       if (this.isStepChange) {
    //         return null;
    //       }
    //       return res;
    //     }),
    //     tap((e) => {
    //       this.checkErrors(this.allFormData);
    //       this.formChanges.emit(this.allFormData);
    //     }),
    //   )
    //   .subscribe();
  }

  resetForm() {
    if (this.resetFormOnSubmit) {
      this.allFormData.reset();
    }
  }

  checkErrors(form: FormGroup) {
    const errors = this.formService.getErrorList(form);
    const errorsKeys = Object.keys(errors);
    this.hasRequiredError = errorsKeys.indexOf('required') >= 0;
    this.hasFillError =
      errorsKeys.length > 1 || (errorsKeys.length === 1 && !this.hasRequiredError);
  }

  openModal(isLoading = false, modalData: ModalConfirmDataElement = {text: '', title: ''}) {
    this.dialogService.closeAll();
    this.modal = this.dialogService.open(
      isLoading ? ModalLoadingComponent : ModalConfirmReceiptComponent,
      {
        showCloseIcon: !isLoading,
        data: isLoading ? null : modalData,
        disableClose: isLoading,
      },
    );
  }

  onCreatedComponent(event: any) {
    if (this.additionalButtonEvent.observed) {
      event.instance.additionaButtonEvent
        .pipe(
          first(),
          tap((el) => {
            this.additionalButtonEvent.emit(el);
          }),
        )
        .subscribe();
    }
    if (this.componentCreated.observed) {
      console.log('comp. created', event);
      event.instance.createdComponents
        .pipe(
          first(),
          filter(Boolean),
          tap((el: ComponentRef<GenericFieldDirective>[]) => {
            this.componentCreated.emit(el);
          }),
        )
        .subscribe();
    }
  }

  ngOnDestroy() {
    this.allFormData = new FormGroup({});
    this.baseFormGroup = new FormGroup({});
    this.formSubscription?.unsubscribe();
    this.formPartSubscription?.unsubscribe();
    this.uploadSubscriber?.unsubscribe();
    this.formstepSubscriber?.unsubscribe();
    this.deleteUploadedFileSubscriber?.unsubscribe();
    // this.fieldService.destroy();
    this.valueChangesObserver$?.unsubscribe();
  }
}
